<template>
  <v-card
    outlined
    class="pa-4 drop-zone elevation-0"
    :class="{ 'drag-over': isDragging }"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
    @click="openFileDialog"
  >
    <v-row align="center" no-gutters>
      <v-col cols="auto" class="flex-grow-1">
        <div class="file-text">
          <span v-if="!file" class="placeholder-text">Arrastre el archivo aquí</span>
          <span v-else class="file-name">{{ file.name }} ({{ formatFileSize(file.size) }})</span>
        </div>
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          accept="image/*,.xlsx,.xls"
          @change="handleFileSelect"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          dark
          @click.stop="handleSubmit"
          :disabled="!file"
          rounded="pill"
          elevation="0"
          append-icon="mdi-upload"
          class="text-none"
        >
          Subir
        </v-btn>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from 'vue'

export default defineComponent({
  name: 'FileUploadComponent',
  emits: ['file-upload'],
  setup(_, { emit }) {
    const file: Ref<File | null> = ref(null)
    const isDragging: Ref<boolean> = ref(false)
    const dragCounter: Ref<number> = ref(0)
    const fileInput: Ref<HTMLInputElement | null> = ref(null)

    const openFileDialog = (): void => {
      fileInput.value?.click()
    }

    const handleFileSelect = (e: Event): void => {
      const target = e.target as HTMLInputElement
      const files = target.files
      if (files && files.length > 0) {
        file.value = files[0]
        onFileChange(files[0])
      }
    }

    const handleDragEnter = (e: DragEvent): void => {
      dragCounter.value++
      isDragging.value = true
    }

    const handleDragOver = (e: DragEvent): void => {
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy'
      }
    }

    const handleDragLeave = (e: DragEvent): void => {
      dragCounter.value--
      if (dragCounter.value === 0) {
        isDragging.value = false
      }
    }

    const handleDrop = (e: DragEvent): void => {
      isDragging.value = false
      dragCounter.value = 0

      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        const droppedFile = files[0]

        // Validar tipo de archivo
        const validTypes: string[] = [
          'image/jpeg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
        ]

        if (
          validTypes.includes(droppedFile.type) ||
          droppedFile.name.match(/\.(jpg|jpeg|png|gif|webp|svg|xlsx|xls)$/i)
        ) {
          file.value = droppedFile
          onFileChange(droppedFile)
        } else {
          alert('Por favor, suba solo imágenes o archivos Excel (.xlsx, .xls)')
        }
      }
    }

    const onFileChange = (selectedFile: File): void => {
    }

    const handleSubmit = (): void => {
      if (file.value) {
        emit('file-upload', file.value)
      }
    }

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    return {
      file,
      isDragging,
      dragCounter,
      fileInput,
      openFileDialog,
      handleFileSelect,
      handleDragEnter,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      onFileChange,
      handleSubmit,
      formatFileSize,
    }
  },
})
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.drop-zone.drag-over {
  border-color: #1976d2;
  background-color: #e3f2fd;
  transform: scale(1.02);
}

.file-text {
  padding: 8px 0;
  min-height: 24px;
}

.placeholder-text {
  color: #666;
  font-size: 14px;
}

.file-name {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}
</style>
